import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";

// Refresh the pre-signed URL once ~90% of its lifetime has elapsed, so a fresh
// URL is in place before the current one expires mid-view/mid-playback.
const REFRESH_RATIO = 0.9;
const MIN_REFRESH_DELAY_MS = 10 * 1000; // never schedule sooner than 10s
const FALLBACK_REFRESH_MS = 50 * 60 * 1000; // if backend sent no expiry, refresh in 50 min
const ERROR_RETRY_MS = 15 * 1000; // backoff after a failed refresh
const MAX_ERROR_REFRESHES = 3; // stop retrying a genuinely broken media

/**
 * Renders advertisement media (video or image) from a private S3 bucket using a
 * short-lived pre-signed URL, transparently regenerating the URL before it
 * expires. For video, playback position and play/pause state are preserved
 * across a URL swap.
 *
 * @param {object}  ad     Advertisement — needs _id, mediaType, mediaUrl, mediaUrlExpiresAt
 * @param {object} [style] Extra styles applied to the media element
 */
const AdMediaPlayer = ({ ad, style }) => {
  const [url, setUrl] = useState(ad.mediaUrl || "");
  const videoRef = useRef(null);

  // Refs so the refresh loop never fights React's render cycle.
  const expiresAtRef = useRef(ad.mediaUrlExpiresAt ? Date.parse(ad.mediaUrlExpiresAt) : null);
  const timerRef = useRef(null);
  const refreshingRef = useRef(false);
  const errorCountRef = useRef(0);
  // Video playback state captured just before a URL swap, restored on reload.
  const pendingSeekRef = useRef(0);
  const wasPlayingRef = useRef(false);

  const isVideo = ad.mediaType === "video";

  const scheduleRefresh = useCallback((refreshFn) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const expiresAt = expiresAtRef.current;
    let delay = FALLBACK_REFRESH_MS;
    if (expiresAt) {
      const lifetime = expiresAt - Date.now();
      delay = Math.max(lifetime * REFRESH_RATIO, MIN_REFRESH_DELAY_MS);
    }
    timerRef.current = setTimeout(refreshFn, delay);
  }, []);

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;

    // Capture video state before the src swaps so we can resume seamlessly.
    const video = videoRef.current;
    if (isVideo && video) {
      pendingSeekRef.current = video.currentTime || 0;
      wasPlayingRef.current = !video.paused && !video.ended;
    }

    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(
        `${CONFIG.BASE_URL}/api/portal/advertisements/${ad._id}/media-url`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed to refresh media URL");
      const data = await res.json();

      expiresAtRef.current = data.expiresAt ? Date.parse(data.expiresAt) : null;
      errorCountRef.current = 0;
      setUrl(data.url);
      scheduleRefresh(refresh);
    } catch {
      // Transient failure — retry with a short backoff rather than dropping the media.
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(refresh, ERROR_RETRY_MS);
    } finally {
      // require-atomic-updates misfires here: this is a re-entrancy mutex (guarded
      // at the top of the function), and we assign a constant `false` rather than a
      // value derived from a possibly-stale read. There is no race to lose.
      // eslint-disable-next-line require-atomic-updates
      refreshingRef.current = false;
    }
  }, [ad._id, isVideo, scheduleRefresh]);

  // Keep in sync if the ad prop changes (e.g. navigating between ads).
  useEffect(() => {
    setUrl(ad.mediaUrl || "");
    expiresAtRef.current = ad.mediaUrlExpiresAt ? Date.parse(ad.mediaUrlExpiresAt) : null;
    errorCountRef.current = 0;
    scheduleRefresh(refresh);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [ad._id, ad.mediaUrl, ad.mediaUrlExpiresAt, refresh, scheduleRefresh]);

  // Restore playback position/state after a URL swap reloads the video.
  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    if (pendingSeekRef.current > 0) {
      try {
        video.currentTime = pendingSeekRef.current;
      } catch {
        /* ignore */
      }
      pendingSeekRef.current = 0;
    }
    if (wasPlayingRef.current) {
      wasPlayingRef.current = false;
      video.play().catch(() => {
        /* autoplay may be blocked; user can press play */
      });
    }
  };

  // A load error most likely means the URL expired — regenerate reactively.
  const handleError = () => {
    if (errorCountRef.current >= MAX_ERROR_REFRESHES) return;
    errorCountRef.current += 1;
    refresh();
  };

  if (!url) return null;

  if (isVideo) {
    return (
      <video
        ref={videoRef}
        controls
        src={url}
        onLoadedMetadata={handleLoadedMetadata}
        onError={handleError}
        style={{ width: "100%", maxHeight: 360, display: "block", background: "#000", ...style }}
      >
        Your browser does not support video.
      </video>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError here is a media-load fallback, not a user interaction
    <img
      src={url}
      alt={ad.advertisementName || "Advertisement"}
      onError={handleError}
      style={{
        width: "100%",
        maxHeight: 360,
        objectFit: "contain",
        display: "block",
        background: "#000",
        ...style,
      }}
    />
  );
};

AdMediaPlayer.propTypes = {
  ad: PropTypes.shape({
    _id: PropTypes.string,
    advertisementName: PropTypes.string,
    mediaType: PropTypes.string,
    mediaUrl: PropTypes.string,
    mediaUrlExpiresAt: PropTypes.string,
  }).isRequired,
  style: PropTypes.object,
};

export default AdMediaPlayer;
